import React, { cloneElement, Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape, type MessageDescriptor } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { Redirect, type RouteComponentProps } from "react-router-dom";
import { isDirty } from "redux-form";
import invariant from "tiny-invariant";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientId } from "~/actions/get_patient_id";
import { getToken } from "~/actions/get_token";
import {
  createCorrection,
  sendPatientCorrection,
  submitCorrection,
  updateCorrection,
} from "~/actions/send_patient_correction";
import { coalesceNone, isNone } from "~/common/assert";
import { isChildrenShortCourse } from "~/common/courses";
import { convertToJSONFullRx } from "~/common/instructions";
import { remoteLog } from "~/common/logging";
import {
  assertIsPatient,
  getLastCorrection,
  getLastSubmittedCorrection,
  hasUnfilledCorrection,
  isPatient,
} from "~/common/patient";
import { IncisorsVerticalOverlap } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { scrollTo } from "~/components/common/ScrollToElement/scrollTo";
import LinksCT from "~/components/patient/addLinks";
import { PatientNewImages } from "~/components/patient/patient_new/patient_new_images";
import {
  isOcclusalPlaneValid,
  PATIENT_NEW_REQUIRED_PHOTOS,
} from "~/components/patient/patient_new_validation";
import { MaterialOptions } from "~/components/patient/patient_shared/material_options";
import { TestPlaticOptions } from "~/components/patient/patient_shared/test_plastic_options";
import { PatientUpdateFiles } from "~/components/patient/patient_update/patient_update_files";
import PatientUpdateUploadCT from "~/components/patient/patient_update/patient_update_upload_ct";
import RecipeForm from "~/components/patient/recipe_redux_form";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import {
  getCourseInstallment,
  isCBCTIncludedInCourse,
  selectCourseInstallment,
  type TCourseInstallment,
} from "~/slices/course-installment";
import { selectCorrectionFormValues, selectLinksFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";

import RouteLeavingGuard from "../patient/route_leaving_guard";

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  patient: state.patient,
  media: state.media,
  media_s3: state.media_s3,
  formValues: selectCorrectionFormValues(state),
  areFormValuesDirty: isDirty("correction")(state),
  links: selectLinksFormValues(state),
  areLinksDirty: isDirty("links")(state),
  courseInstallment: selectCourseInstallment(state),
});

const mapDispatchToProps = {
  eraseStateProp,
  getPatient: getPatientId,
  getToken,
  getCourseInstallment,
  sendPatientCorrection,
  createCorrection,
  updateCorrection,
  submitCorrection,
};

type CorrectionPageProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }>;

type CorrectionPageState = {
  stage: number | null;
  test_plastic: boolean | null;
  with_ct: boolean | null;
  isDirty: boolean;
  isSubmitPending: boolean;
  isDraftPending: boolean;
};

class CorrectionPage extends Component<CorrectionPageProps, CorrectionPageState> {
  initialStateValues: Partial<CorrectionPageState> = {};

  constructor(props: CorrectionPageProps) {
    super(props);
    this.state = {
      stage: null,
      test_plastic: null,
      with_ct: null,
      isDirty: false,
      isSubmitPending: false,
      isDraftPending: false,
    };
    this.submitCorrection = this.submitCorrection.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_correction_page");
  }

  async componentDidMount() {
    this.props.eraseStateProp("media");
    window.scrollTo(0, 0);

    const { patient_id } = this.props.match.params;

    void this.props.getToken();
    void this.props.getCourseInstallment(Number(patient_id));

    setDocumentTitle(this.props.intl.formatMessage({ id: "treatment.correct.header" }), patient_id);

    const patient = await this.props.getPatient(Number(patient_id));

    if (patient) {
      const { test_plastic, with_ct } = getLastCorrection(patient);
      const { steps_count_completed, current_step_number } = getLastSubmittedCorrection(patient);
      const stage = current_step_number ?? coalesceNone(steps_count_completed, null);
      this.setState({ stage, test_plastic, with_ct });
      this.initialStateValues = { stage, test_plastic, with_ct };
    }
  }

  componentDidUpdate() {
    if (this.getIsFormDirty()) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  componentWillUnmount() {
    window.onbeforeunload = null;
  }

  validateRequiredFields(patient: TPatient, courseInstallment: TCourseInstallment) {
    const {
      formValues: { arch, material, vertical_overlap, vertical_overlap_comment } = {},
      media,
    } = this.props;
    const { stage, with_ct } = this.state;

    const lastSubmittedCorrection = getLastSubmittedCorrection(patient);

    const isDeepCBCT =
      (!isNone(patient.course.course_id) &&
        isCBCTIncludedInCourse(courseInstallment, patient.course.course_id)) ||
      patient.deep_cbct;

    const isChildrenShort = isChildrenShortCourse(patient.course.course_id);

    return {
      arch: arch != null,
      material: material != null,
      vertical_overlap_comment:
        vertical_overlap == IncisorsVerticalOverlap.INCREASE ||
        vertical_overlap == IncisorsVerticalOverlap.DECREASE
          ? Boolean(vertical_overlap_comment)
          : null,
      with_ct: !isChildrenShort && isDeepCBCT ? with_ct != null : null,
      stage: !isChildrenShort ? typeof stage == "number" && stage > 0 : null,
      occlusal_plane: isOcclusalPlaneValid(this.props.formValues),
      images: PATIENT_NEW_REQUIRED_PHOTOS.every(
        (item) =>
          (media[item] && media[item]?.md5) ||
          patient.media.correction_media[lastSubmittedCorrection.index + 1]?.[item],
      ),
    } satisfies Record<string, boolean | null>;
  }

  async submitCorrection() {
    assertIsPatient(this.props.patient);
    invariant(this.props.formValues, "expected formValues to be defined");

    if (hasUnfilledCorrection(this.props.patient)) {
      return await this.updateCorrection({ submit: true });
    }

    const { patient_id } = this.props.patient;
    const rxTypeId = this.props.patient.rx_type_id;

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => Object.values(link));

    const prescription = convertToJSONFullRx(this.props.formValues);
    prescription.rx_type_id = rxTypeId;

    const { stage, with_ct } = this.state;
    invariant(typeof stage == "number" && stage > 0, "Expected `stage` to be a positive number");

    this.setState({ isSubmitPending: true });
    try {
      await this.props.sendPatientCorrection(
        patient_id,
        this.props.media,
        this.props.media_s3,
        links,
        stage,
        prescription,
        with_ct,
        this.state.test_plastic,
      );
      this.props.eraseStateProp("patient");
      this.props.history.push("/pages/patients");
    } catch {
      this.setState({ isSubmitPending: false });
    }
  }

  async createCorrection() {
    assertIsPatient(this.props.patient);
    invariant(this.props.formValues, "expected formValues to be defined");

    const { patient_id } = this.props.patient;
    const rxTypeId = this.props.patient.rx_type_id;

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => Object.values(link));

    const prescription = convertToJSONFullRx(this.props.formValues);
    prescription.rx_type_id = rxTypeId;

    const { stage, with_ct } = this.state;
    invariant(typeof stage == "number" && stage > 0, "Expected `stage` to be a positive number");

    this.setState({ isDraftPending: true });
    try {
      await this.props.createCorrection({
        patient_id,
        media_info: this.props.media,
        s3_media: this.props.media_s3,
        links,
        stage,
        prescription,
        with_ct,
        test_plastic: this.state.test_plastic,
      });
      this.props.eraseStateProp("patient");
      this.props.history.push("/pages/patients");
    } catch {
      this.setState({ isDraftPending: false });
    }
  }

  async updateCorrection({ submit = false } = {}) {
    assertIsPatient(this.props.patient);
    invariant(this.props.formValues, "expected formValues to be defined");

    const { patient_id } = this.props.patient;
    const rxTypeId = this.props.patient.rx_type_id;

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => Object.values(link));

    const prescription = convertToJSONFullRx(this.props.formValues);
    prescription.rx_type_id = rxTypeId;

    const { stage, with_ct } = this.state;
    invariant(typeof stage == "number" && stage > 0, "Expected `stage` to be a positive number");

    const correction_id = getLastCorrection(this.props.patient).id;

    if (submit) {
      this.setState({ isSubmitPending: true });
    } else {
      this.setState({ isDraftPending: true });
    }

    try {
      await this.props.updateCorrection({
        patient_id,
        correction_id,
        media_info: this.props.media,
        s3_media: this.props.media_s3,
        links,
        stage,
        prescription,
        with_ct,
        test_plastic: this.state.test_plastic,
      });
      const redirectTo = submit ? "/pages/patients" : `/pages/patient/${patient_id}`;
      if (submit) {
        await this.props.submitCorrection({ patient_id, correction_id });
      }
      this.props.eraseStateProp("patient");
      this.props.history.push(redirectTo);
    } catch {
      if (submit) {
        this.setState({ isSubmitPending: false });
      } else {
        this.setState({ isDraftPending: false });
      }
    }
  }

  setStateValue<T extends "stage" | "test_plastic" | "with_ct">(
    key: T,
    value: CorrectionPageState[T],
  ) {
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
      isDirty: value != this.initialStateValues[key],
    }));
  }

  getIsFormDirty(): boolean {
    const { media, media_s3, areFormValuesDirty, areLinksDirty } = this.props;
    const { isDirty } = this.state;

    const isMediaDirty = Object.keys(media).length > 0;
    const isS3MediaDirty = Array.isArray(media_s3.files) && media_s3.files.length > 0;

    return isDirty || areFormValuesDirty || areLinksDirty || isMediaDirty || isS3MediaDirty;
  }

  render() {
    const { patient, courseInstallment } = this.props;
    const { stage, test_plastic, with_ct, isSubmitPending, isDraftPending } = this.state;

    if (!isPatient(patient) || !courseInstallment) {
      return (
        <Layout>
          <Loader />
        </Layout>
      );
    }

    const lastCorrection = getLastCorrection(patient);
    const lastSubmittedCorrection = getLastSubmittedCorrection(patient);

    if (!lastCorrection.order_options.can_order_correction && !hasUnfilledCorrection(patient)) {
      return <Redirect to="/pages/patients" />;
    }

    const isDeepCBCT =
      (!isNone(patient.course.course_id) &&
        isCBCTIncludedInCourse(courseInstallment, patient.course.course_id)) ||
      patient.deep_cbct;

    const isChildrenShort = isChildrenShortCourse(patient.course.course_id);
    const optedOutOfTestPlastic =
      lastSubmittedCorrection.test_plastic == true && test_plastic == false;

    const validity = this.validateRequiredFields(patient, courseInstallment);
    const isFormDirty = this.getIsFormDirty();

    return (
      <Layout>
        <Portlet as="main">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage
              id={
                isChildrenShort ? "treatment.additional_course.header" : "treatment.correct.header"
              }
            />
          </PortletTitle>

          <RouteLeavingGuard
            when={isFormDirty}
            shouldBlockNavigation={() => isFormDirty}
            saveChanges={() =>
              hasUnfilledCorrection(patient) ? this.updateCorrection() : this.createCorrection()
            }
            saveAsADraft={false}
          />

          <div className="portlet-body">
            {isChildrenShort ? null : (
              <StageInput
                maxValue={coalesceNone(lastSubmittedCorrection.steps_count_total, 0)}
                defaultValue={stage}
                onValueChange={(value) => this.setStateValue("stage", value)}
              />
            )}

            <MaterialOptions />

            {canOrderTestPlastic(this.props.user) &&
            lastSubmittedCorrection.test_plastic != null ? (
              <TestPlaticOptions
                value={test_plastic}
                onValueChange={(value) => this.setStateValue("test_plastic", value)}
              />
            ) : null}

            {!isChildrenShort && isDeepCBCT ? (
              <WithCTOptions
                value={with_ct}
                onValueChange={(value) => this.setStateValue("with_ct", value)}
              />
            ) : null}

            <PatientNewImages
              displayHeading={false}
              correctionNumber={lastSubmittedCorrection.index + 1}
              patient_id={patient.patient_id}
            />
            <PatientUpdateFiles
              patient={patient}
              has_medit_files={false}
              showUploadedFiles={hasUnfilledCorrection(patient)}
            />
            <PatientUpdateUploadCT showUploadedFiles={hasUnfilledCorrection(patient)} />
            <LinksCT />
            <RecipeForm course_id={patient.course.course_id} />
            <CheckList validity={validity} />

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-border-t tw-border-[#e7ecf1] tw-py-5 sm:tw-gap-2">
              <Wrapper
                when={optedOutOfTestPlastic}
                element={
                  <ConfirmationDialog
                    title={<FormattedMessage id="pat.status.button.confirm" />}
                    description={
                      <FormattedMessage
                        id="plan.cancel.opt_out_test_plastic.description"
                        values={{ bold: (chunks) => <strong>{chunks}</strong> }}
                      />
                    }
                    actionText={
                      <FormattedMessage
                        id={isChildrenShort ? "plan.order.btn" : "plan.cancel.btn"}
                      />
                    }
                    onActionClick={(closeDialog) => (closeDialog(), this.submitCorrection())}
                  />
                }
              >
                <LoadingButton
                  type="submit"
                  variant="primary"
                  size="xl"
                  className="tw-w-full sm:tw-max-w-[200px]"
                  disabled={Object.values(validity).some((v) => v == false) || isDraftPending}
                  onClick={optedOutOfTestPlastic ? undefined : () => this.submitCorrection()}
                  isLoading={isSubmitPending}
                >
                  <FormattedMessage id={isChildrenShort ? "plan.order.btn" : "plan.cancel.btn"} />
                </LoadingButton>
              </Wrapper>

              <LoadingButton
                type="button"
                variant="secondary"
                size="xl"
                className="tw-w-full sm:tw-max-w-[200px]"
                disabled={isSubmitPending}
                onClick={() =>
                  hasUnfilledCorrection(patient) ? this.updateCorrection() : this.createCorrection()
                }
                isLoading={isDraftPending}
              >
                <FormattedMessage id="BTN_SAVE_DRAFT" />
              </LoadingButton>
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(CorrectionPage));

function CheckList({
  validity,
}: {
  validity: ReturnType<CorrectionPage["validateRequiredFields"]>;
}) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-6 tw-py-3">
      {validity.stage != null ? (
        <CheckListItem
          intlId="recipe.check.reference.steps"
          toId="validation-stage"
          isValid={validity.stage}
        />
      ) : null}

      <CheckListItem
        intlId="recipe.check.reference.material"
        toId="validation-material"
        isValid={validity.material}
      />

      {validity.with_ct != null ? (
        <CheckListItem
          intlId="recipe.check.reference.correction_with_ct"
          toId="validation-correction-with-ct"
          isValid={validity.with_ct}
        />
      ) : null}

      <CheckListItem
        intlId="recipe.check.reference.photos"
        toId="profile"
        isValid={validity.images}
      />

      <CheckListItem
        intlId="recipe.check.reference.arches"
        toId="validation-arch"
        isValid={validity.arch}
      />

      <CheckListItem
        intlId="TA_OCCLUSAL"
        toId="validation-occlusal_plane"
        isValid={validity.occlusal_plane}
      />

      {validity.vertical_overlap_comment != null ? (
        <CheckListItem
          intlId="INCISORS_VO"
          toId="incisors-vert-overlap-label"
          isValid={validity.vertical_overlap_comment}
        />
      ) : null}
    </div>
  );
}

function CheckListItem({
  intlId,
  toId,
  isValid,
}: {
  intlId: MessageDescriptor["id"];
  toId: string;
  isValid: boolean;
}) {
  const doneStyles = { color: "#82D9D4", fontWeight: 700 };
  const unDoneStyles = { color: "red", fontWeight: 500 };

  return (
    <div className="scroll-block tw-w-fit">
      <FormattedMessage tagName="p" id="plan.cancel.scroll.to.field" />

      <button
        className="scrollBtn tw-flex tw-min-w-[200px] tw-items-center tw-gap-2"
        onClick={() => scrollTo({ id: toId, duration: 1500 })}
      >
        <i className="icon-check" style={isValid ? doneStyles : unDoneStyles} />
        <FormattedMessage id={intlId} />
      </button>
    </div>
  );
}

function StageInput({
  maxValue,
  defaultValue,
  onValueChange,
}: {
  maxValue: number;
  defaultValue: number | null;
  onValueChange(value: number | null): void;
}) {
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { valueAsNumber } = event.target;

    if (valueAsNumber > 0 && valueAsNumber <= maxValue) {
      onValueChange(valueAsNumber);
    } else {
      event.target.value = "";
      onValueChange(null);
    }
  }

  return (
    <div className="form-group">
      <Label id="validation-stage" htmlFor="correction-stage-value" required>
        <FormattedMessage id="plan.cancel.stage" />
      </Label>

      <br />
      <Input
        id="correction-stage-value"
        type="number"
        size={2}
        min={1}
        max={maxValue}
        className="tw-inline tw-max-w-[80px]"
        name="stage"
        defaultValue={defaultValue ?? undefined}
        onChange={handleChange}
      />
      <span>
        {" "}
        <FormattedMessage id="pat_table.of" /> {maxValue}
      </span>
    </div>
  );
}

function WithCTOptions({
  value,
  onValueChange,
}: {
  value: boolean | null;
  onValueChange(value: boolean): void;
}) {
  const options: { value: boolean; intlId: MessageDescriptor["id"] }[] = [
    { value: true, intlId: "C_YES_CT_CORRECTION" },
    { value: false, intlId: "C_NO_CT_CORRECTION" },
  ];

  return (
    <LabelledRadioGroup
      label={<FormattedMessage id="ATTACHMENT_INSTRUCTIONS" />}
      required
      className="form-group"
    >
      {options.map((option) => (
        <LabelledRadio
          key={option.intlId}
          label={<FormattedMessage id={option.intlId} />}
          name="with_ct"
          checked={option.value == value}
          onChange={() => onValueChange(option.value)}
        />
      ))}
    </LabelledRadioGroup>
  );
}

function Wrapper({
  when,
  element,
  children,
}: React.PropsWithChildren<{ when: boolean; element: React.ReactElement }>) {
  return when ? cloneElement(element, {}, children) : <>{children}</>;
}
